import { type FormEvent, useEffect, useState } from "react";

import AppShell, { ErrorNote } from "@/components/AppShell";
import NumericInput from "@/components/NumericInput";
import PostalCodeField from "@/components/PostalCodeField";
import ReportButton from "@/components/ReportButton";
import { Link } from "react-router-dom";

import { api } from "@/lib/api";
import CyaPlanSection from "@/components/CyaPlanSection";
import { useAsync } from "@/lib/hooks";
import { TEST_KITS } from "@/lib/testKits";
import type { AppSettings, GeocodeResult, Remediation, TestKit, Vessel } from "@/types";

export function exportUrl(vesselId: number): string {
  return `/api/vessels/${vesselId}/export`;
}

export default function Settings() {
  const pool = useAsync<Vessel>(() => api.get<Vessel>(""), []);
  const settings = useAsync<AppSettings>(() => api.get<AppSettings>("/api/settings"), []);
  // Decoration on this screen: a failure here must not stop you saving your
  // pool volume, so it falls back to null rather than taking the page down.
  const [cyaStage, setCyaStage] = useState("");
  const remediation = useAsync<Remediation | null>(
    () =>
      api
        .get<Remediation>(
          // The typed depth previews without being saved, so "how many trips at
          // 18 inches?" is answered as you type rather than after a round trip.
          cyaStage.trim() === ""
            ? "/api/cya-remediation"
            : `/api/cya-remediation?stage_inches=${encodeURIComponent(cyaStage.trim())}`,
        )
        .catch(() => null),
    [cyaStage],
  );

  const [name, setName] = useState("");
  const [volume, setVolume] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [place, setPlace] = useState<GeocodeResult | null>(null);
  const [chlorine, setChlorine] = useState("");
  const [tubChlorine, setTubChlorine] = useState("");
  const [cleanPressure, setCleanPressure] = useState("");
  const [staleDays, setStaleDays] = useState("");
  const [cyaApproach, setCyaApproach] = useState("");
  const [waterRate, setWaterRate] = useState("");
  const [cyaStarted, setCyaStarted] = useState("");
  const [testKit, setTestKit] = useState<TestKit>("taylor_k2006");
  const [rainSuggestions, setRainSuggestions] = useState(true);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (pool.data) {
      setName(pool.data.name);
      setVolume(pool.data.volume_gallons);
      setPostalCode(pool.data.postal_code ?? "");
    }
  }, [pool.data]);

  useEffect(() => {
    if (settings.data) {
      setChlorine(settings.data.liquid_chlorine_percent);
      setTubChlorine(settings.data.strength_overrides?.liquid_chlorine ?? "");
      setCleanPressure(settings.data.filter_clean_pressure_psi ?? "");
      setStaleDays(String(settings.data.stale_reading_days));
      setCyaApproach(settings.data.cya_plan_approach ?? "");
      setCyaStage(settings.data.cya_drain_stage_inches ?? "");
      setWaterRate(settings.data.water_cost_per_1000_gal ?? "");
      setCyaStarted(settings.data.cya_plan_started_on ?? "");
      setTestKit(settings.data.test_kit);
      setRainSuggestions(settings.data.rain_suggestions_enabled);
    }
  }, [settings.data]);

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!pool.data || !settings.data) return;
    setBusy(true);
    setError(null);
    setSaved(false);

    try {
      // Coordinates only ever move together with the ZIP that produced them.
      // Writing one without the other would leave the pool row claiming a
      // location it had not resolved, which is the state this whole field
      // exists to get out of.
      await api.put<Vessel>("", {
        ...pool.data,
        // Trimmed, and never blank: the name is the dashboard heading, and an
        // empty one leaves the screen with no title at all.
        name: name.trim() || pool.data.name,
        volume_gallons: volume,
        postal_code: postalCode.trim() === "" ? null : postalCode.trim(),
        ...(place
          ? {
              latitude: place.latitude,
              longitude: place.longitude,
              // Blank means the provider gave none, so the existing zone stands
              // rather than being replaced with an empty string.
              timezone: place.timezone || pool.data.timezone,
            }
          : {}),
      });
      await api.put<AppSettings>("/api/settings", {
        ...settings.data,
        liquid_chlorine_percent: chlorine,
        // Blank means "the household jug", which is an absent key rather than
        // an empty string — the server falls through to the shared value.
        strength_overrides: tubChlorine.trim() === ""
          ? {}
          : { liquid_chlorine: tubChlorine.trim() },
        filter_clean_pressure_psi: cleanPressure === "" ? null : cleanPressure,
        stale_reading_days: Number(staleDays),
        // Blank means "no preference", which is a null rather than an empty
        // string — the server computes the safe drain depth when it is unset.
        cya_plan_approach: cyaApproach === "" ? null : cyaApproach,
        cya_drain_stage_inches: cyaStage.trim() === "" ? null : cyaStage.trim(),
        water_cost_per_1000_gal: waterRate.trim() === "" ? null : waterRate.trim(),
        cya_plan_started_on: cyaStarted === "" ? null : cyaStarted,
        test_kit: testKit,
        rain_suggestions_enabled: rainSuggestions,
      });
      remediation.reload();
      // Re-read rather than keeping the resolved place in hand: once it is
      // stored, `hasCoordinates` is what should drive the field, and a stale
      // "save to use it" prompt under a value already saved is noise.
      pool.reload();
      setPlace(null);
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save your changes.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AppShell title="Setup" subtitle={pool.data?.location ?? undefined}>
      {(pool.error || settings.error) && (
        <ErrorNote message={pool.error ?? settings.error ?? ""} />
      )}

      {pool.data && settings.data && (
        <form onSubmit={save} className="space-y-4">
          {/* Two scopes, said out loud. The split is not cosmetic — it is
              exactly the `VesselSettings` / `AppSettings` line (ADR 0015), and
              putting a shared field beside a per-vessel one with nothing to
              tell them apart is what made "chlorine strength" and "strength for
              this pool only" read as the same setting twice.

              Headed sections rather than tabs: this is a 390px screen, and a
              tab hides the very thing the reader needs to know — that changing
              a value here will or will not move the other pool. */}
          <p className="unit">This pool or tub</p>

          <section className="card space-y-4 p-4">
            <h2 className="text-sm font-semibold">
              {pool.data.vessel_type === "hot_tub" ? "Hot tub" : "Pool"}
            </h2>
            <Field label="Name" htmlFor="name" hint="What the dashboard is titled.">
              <input
                id="name"
                className="field"
                maxLength={120}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Field>
            <Field
              label="Volume (gallons)"
              htmlFor="volume"
              hint="Every suggested dose divides by this."
            >
              <NumericInput
                id="volume"
                className="field figure"
                value={volume}
                onChange={setVolume}
              />
            </Field>

            <PostalCodeField
              value={postalCode}
              onChange={setPostalCode}
              resolved={place}
              onResolved={(next) => {
                setPlace(next);
                setPostalCode(next.postal_code);
              }}
              hasCoordinates={pool.data.latitude !== null && pool.data.longitude !== null}
            />
          </section>

          <section className="card space-y-4 p-4">
            <h2 className="text-sm font-semibold">Chemicals</h2>
            <Field
              label="Liquid chlorine strength (%)"
              htmlFor="chlorine-override"
              hint="Leave blank to use the shared strength, set below. Fill it in when this water uses a different product — a small hot tub is usually dosed with 8.25% household bleach, because 12.5% is too concentrated to measure at that volume."
            >
              <NumericInput
                id="chlorine-override"
                placeholder="shared"
                className="field figure"
                value={tubChlorine}
                onChange={setTubChlorine}
              />
            </Field>
          </section>

          <section className="card space-y-4 p-4">
            <h2 className="text-sm font-semibold">Warnings</h2>
            {pool.data.vessel_type !== "hot_tub" && (
            <Field
              label="Clean filter pressure (psi)"
              htmlFor="clean-pressure"
              hint="Read this right after a backwash. Without it the app can't tell you when to backwash again."
            >
              <NumericInput
                id="clean-pressure"
                placeholder="Not set"
                className="field figure"
                value={cleanPressure}
                onChange={setCleanPressure}
              />
            </Field>
            )}
            <Field label="Remind me if I haven't tested in (days)" htmlFor="stale-days">
              <NumericInput
                id="stale-days"
                decimal={false}
                className="field figure"
                value={staleDays}
                onChange={setStaleDays}
              />
            </Field>
          </section>

          <CyaPlanSection
            remediation={remediation.data ?? null}
            approach={cyaApproach}
            stageInches={cyaStage}
            waterRate={waterRate}
            startedOn={cyaStarted}
            onApproach={setCyaApproach}
            onStageInches={setCyaStage}
            onWaterRate={setWaterRate}
            onStartedOn={setCyaStarted}
          />

          <p className="unit pt-2">Shared by every pool and tub</p>

          <section className="card space-y-4 p-4">
            <h2 className="text-sm font-semibold">Testing and weather</h2>
            <Field
              label="Test kit"
              htmlFor="test-kit"
              hint="Picks which testing instructions the guide shows. It never changes what a reading means — that is recorded per reading, as the method you used."
            >
              <select
                id="test-kit"
                className="field"
                value={testKit}
                onChange={(e) => setTestKit(e.target.value as TestKit)}
              >
                {TEST_KITS.map((kit) => (
                  <option key={kit.value} value={kit.value}>
                    {kit.label}
                  </option>
                ))}
              </select>
              <Link to="/guide/testing" className="mt-1 inline-block text-xs underline">
                Testing procedures for this kit
              </Link>
            </Field>
            <label className="flex items-start gap-3 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={rainSuggestions}
                onChange={(e) => setRainSuggestions(e.target.checked)}
              />
              <span>
                <span className="font-medium">Suggest rain days from the weather service</span>
                <span className="block text-xs text-(--color-ink-faint)">
                  Off if you log rain from your own gauge and never want the prompt. Either way
                  the app only records rain you log yourself.
                </span>
              </span>
            </label>
          </section>

          <section className="card space-y-4 p-4">
            <h2 className="text-sm font-semibold">The shed</h2>
            <Field
              label="Liquid chlorine strength (%)"
              htmlFor="chlorine"
              hint="The jug you actually buy. One value for the whole house — changing it here changes what every pool and tub is dosed at, which is the point: two copies free to drift is a 25% dosing error waiting to happen."
            >
              <NumericInput
                id="chlorine"
                className="field figure"
                value={chlorine}
                onChange={setChlorine}
              />
            </Field>
          </section>

          {error && <ErrorNote message={error} />}
          {saved && <p className="text-sm text-(--color-status-ok)">Saved.</p>}

          <button type="submit" disabled={busy} className="btn btn-primary w-full">
            {busy ? "Saving…" : "Save changes"}
          </button>

          <section className="card space-y-3 p-4">
            <h2 className="text-sm font-semibold">Reports</h2>
            <p className="text-xs text-(--color-ink-faint)">
              The spec sheet is the one-pager you want when calling for a part or filing a
              warranty claim.
            </p>
            <ReportButton path="/api/reports/spec-sheet" label="Pool & equipment spec sheet" />
            <ReportButton
              path={`/api/reports/season/${new Date().getFullYear()}`}
              label={`${new Date().getFullYear()} season summary`}
            />
          </section>

          <section className="card space-y-3 p-4">
            <h2 className="text-sm font-semibold">Your data</h2>
            <p className="text-xs text-(--color-ink-faint)">
              One file with everything about this {pool.data.vessel_type === "hot_tub" ? "hot tub" : "pool"}:
              its settings, every reading, dose, event, piece of equipment, pump schedule, photo
              and conversation. Importing it on any Pool Nerd account creates a new pool from it.
            </p>
            <a
              href={exportUrl(pool.data.id)}
              download
              className="btn btn-quiet block w-full text-center"
            >
              Export everything as a file
            </a>
            <Link to="/import" className="block text-center text-xs underline">
              Import a pool or hot tub from a file
            </Link>
          </section>

          <p className="text-center text-xs text-(--color-ink-faint)">
            Signing out and deleting your account live under{" "}
            <Link to="/account" className="underline">
              Account
            </Link>
            , in the menu at the top right.
          </p>
        </form>
      )}
    </AppShell>
  );
}

function Field({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label htmlFor={htmlFor} className="text-sm font-medium">
        {label}
      </label>
      <div className="mt-1.5">{children}</div>
      {hint && <p className="mt-1.5 text-xs text-(--color-ink-faint)">{hint}</p>}
    </div>
  );
}
